import type React from 'react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { CLOSED_STATUSES, getObjectLabels, getQuoteNextPresentation } from '@paintscout/util/builder'
import { useClientOptions } from '@paintscout/ui-business'
import PrintIcon from '@material-ui/icons/PrintOutlined'
import DownloadIcon from '@material-ui/icons/PictureAsPdfOutlined'
import DeclineIcon from '@material-ui/icons/CancelOutlined'
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined'
import striptags from 'striptags'
import { usePresentationNav } from '../PresentationNavContext'
import { hasTermsBlock } from '../../util'
import { QuoteContext } from '../../../context/QuoteContext'

export interface PresentationMoreAction {
  key: 'terms' | 'print' | 'download' | 'decline'
  label: string
  icon: React.ComponentType<{ className?: string }>
  onSelect: (ev: React.MouseEvent<HTMLElement>) => void
}

/**
 * The "More Actions" items — View Terms, Print, Download, Decline — and when each one shows. Shared by
 * the accept button's menu and the phone Pages drawer, which lists them instead of the menu on the
 * customer view (ENG-2075).
 *
 * Data only: "View Terms" opens a dialog, so the caller renders `PresentationTermsDialog` when
 * `hasTerms`, driven by `termsOpen` / `closeTerms`.
 */
export function usePresentationMoreActions() {
  const { options } = useClientOptions()
  const { view, allowResponse, onMoreClick, isPreviewAccept, presentation: navPresentation } = usePresentationNav()
  const { quote } = useContext(QuoteContext)
  const objectLabels = getObjectLabels({ options, quote })

  // CLOSED_STATUSES is a general-purpose "terminal workflow status" list, but 'onHold' is meant to
  // be re-openable from the customer's own perspective — the v1 presentation's equivalent gate
  // (getAllowResponse in PresentationNavContextProvider.tsx) never blocked accept/decline for it
  // either. Treating it as closed here disabled Accept/Decline permanently until the estimate was
  // re-sent, even after a status change back to open (#22795).
  const hasOpenStatus = quote?.status?.value === 'onHold' || !CLOSED_STATUSES.includes(quote?.status?.value)

  // Decline makes no sense for an estimator previewing their own draft — exclude it once
  // onMoreClick is wired through the Preview/Accept dialog (#22699 follow-up).
  const allowDecline =
    allowResponse && options?.options?.allowDecline && hasOpenStatus && !quote.is_invoice && !isPreviewAccept

  // if the presentation already renders a terms block inline, don't duplicate it in this menu —
  // the quote drawer offers "View Terms" instead in that case (see PresentationQuoteDrawer)
  //
  // The nav context carries the presentation that is on screen, so it wins over the lookup, the
  // same way Presentation.tsx prefers its own `presentation` prop. A preview renders the quote's
  // snapshot or the builder's live Formik values, while `current: true` returns the company's saved
  // template — when only the rendered one holds the terms block, the menu offered "View Terms" on
  // top of terms already visible (#22699). The lookup stays as the fallback.
  const presentation = useMemo(
    () =>
      navPresentation ??
      getQuoteNextPresentation({ options, quote, current: true, isWorkOrder: view === 'work-order' }),
    [navPresentation, options, quote, view]
  )
  const hasTerms = !!striptags(quote.terms?.content) && !hasTermsBlock(presentation)

  // "View Terms" is handled here rather than through onMoreClick: the item only shows when the
  // presentation has no terms block, so the terms are either inside the Quick Cart drawer's own
  // scroll pane or not on screen at all. A dialog covers both; scrolling to #terms covers neither.
  const [termsOpen, setTermsOpen] = useState(false)

  // the quote can update while the customer sits on the page, so hasTerms can flip to false with
  // the dialog open — close it rather than leaving termsOpen stale for the next mount
  useEffect(() => {
    if (!hasTerms) setTermsOpen(false)
  }, [hasTerms])

  const docLabel =
    view === 'work-order' ? objectLabels.workOrder.value : quote?.is_invoice ? 'Invoice' : objectLabels.quote.value

  const actions: PresentationMoreAction[] = []
  if (onMoreClick) {
    if (hasTerms) {
      actions.push({ key: 'terms', label: 'View Terms', icon: ListAltOutlinedIcon, onSelect: () => setTermsOpen(true) })
    }
    actions.push({
      key: 'print',
      label: `Print ${docLabel}`,
      icon: PrintIcon,
      onSelect: (ev) => onMoreClick(ev, 'print')
    })
    // Preview/Accept's onMoreClick only implements 'print' — the preview quote may be unsaved, and
    // download needs a persisted _id/_rev, so the item is hidden there rather than left as a click
    // that goes nowhere (#22699 follow-up).
    if (!isPreviewAccept) {
      actions.push({
        key: 'download',
        label: `Download ${docLabel}`,
        icon: DownloadIcon,
        onSelect: (ev) => onMoreClick(ev, 'download')
      })
    }
    if (allowDecline) {
      actions.push({
        key: 'decline',
        label: getDeclineLabel(),
        icon: DeclineIcon,
        onSelect: (ev) => onMoreClick(ev, 'decline')
      })
    }
  }

  const closeTerms = () => setTermsOpen(false)

  return { actions, hasTerms, termsOpen, closeTerms, hasOpenStatus, presentation }

  function getDeclineLabel() {
    if (objectLabels.declineButton) {
      return objectLabels.declineButton.value
    }
    const item =
      quote?.is_invoice && quote?.totals?.pending ? objectLabels.additionalWork.value : objectLabels.quote.value
    return `Decline ${item}`
  }
}

export default usePresentationMoreActions
